import {
  applySizeToImageUrl,
  Button,
  ButtonVariant,
  Device,
  Icon,
  IconName,
  ImageSizeVariant,
  InfoBox,
  RadioButton,
  RadioButtonList,
  ResponsiveTypography,
  ResponsiveTypographyVariant,
  Scale,
  useDeviceViewport,
} from '@se-admin/falcon';
import { i18n } from '@secret-escapes/i18n';
import { History } from 'history';
import * as moment from 'moment';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { TranslationsContext } from '../../common/contexts';
import { ApplicationContext, IApplication } from '../../common/contexts/ApplicationContext';
import {
  CancellationPolicyKey,
  ICancellationPolicyProperty,
  IPageName,
} from '../../common/types/apiSchema';
import {
  BookingDetailsDatesArrowHead,
  BookingDetailsDatesArrowLine,
  BookingDetailsDatesArrowWrapper,
} from '../../common/utils/dateArrow.styles';
import { getLocaleSettings } from '../../common/utils/getLocaleSettingsUtils';
import { LoadingSkeletonSection } from '../../common/utils/loadingSkeletons.styles';
import { getPaths } from '../../constants/paths/paths';
import { AspectRatioWrapper } from '../SaleCard/SaleCardSkeleton/SaleCardSkeleton.styles';
import {
  IBookingDetailsImageProps,
  IBookingDetailsPageProps,
} from './BookingDetailsPage.interfaces';
import {
  BookingDatesItemWrapper,
  BookingDetailsButtonWrapper,
  BookingDetailsDatesWrapper,
  BookingDetailsHotelDetailslWrapper,
  BookingDetailsHotelWrapper,
  BookingDetailsImageWrapper,
  BookingDetailsOccupancyWrapper,
  BookingDetailsTransactionWrapper,
  BookingNotCancellableWrapper,
  CustomNormalImage,
  HorizontalSeperator,
} from './BookingDetailsPage.styles';
import { getHotelAddressString, getOccupancyString } from './BookingDetailsPage.utils';

const DATE_FORMAT: string = 'ddd DD MMM';

const BookingDetailsHeader: React.FunctionComponent<IBookingDetailsPageProps> = ({ booking }) => {
  const location: string = `${booking.saleLocation.city.name}, ${booking.saleLocation.country.name}`;

  return (
    <div>
      <ResponsiveTypography variant={ResponsiveTypographyVariant.micro}>
        {location}
      </ResponsiveTypography>
      <ResponsiveTypography variant={ResponsiveTypographyVariant.t5Serif}>
        {booking.title}
      </ResponsiveTypography>
    </div>
  );
};

const BookingDetailsOccupancy: React.FunctionComponent<IBookingDetailsPageProps> = ({
  booking,
}) => {
  const { translations } = React.useContext(TranslationsContext);
  const occupancy: string = getOccupancyString(booking.occupancy);
  const bookingDetails: string = `${occupancy}${i18n(
    'common.numberOfRooms.text',
    [booking.rooms],
    translations
  )}`;

  return (
    <div>
      <BookingDetailsOccupancyWrapper>
        <Icon name={IconName.adult} />
        <ResponsiveTypography variant={ResponsiveTypographyVariant.body}>
          {bookingDetails}
        </ResponsiveTypography>
      </BookingDetailsOccupancyWrapper>
      <HorizontalSeperator />
    </div>
  );
};

const BookingDetailsDatesArrow: React.FunctionComponent = () => {
  return (
    <BookingDetailsDatesArrowWrapper>
      <BookingDetailsDatesArrowLine />
      <BookingDetailsDatesArrowHead />
    </BookingDetailsDatesArrowWrapper>
  );
};

const BookingDetailsDates: React.FunctionComponent<IBookingDetailsPageProps> = ({ booking }) => {
  const { translations } = React.useContext(TranslationsContext);
  const applicationProps: IApplication = React.useContext(ApplicationContext);
  const locale: string = getLocaleSettings(applicationProps.territoryConfig.posLocale);
  const startDate: string = moment(booking.bookingDates.start)
    .locale(locale)
    .format(DATE_FORMAT);
  const endDate: string = moment(booking.bookingDates.end)
    .locale(locale)
    .format(DATE_FORMAT);

  return (
    <div>
      <BookingDetailsDatesWrapper>
        <BookingDatesItemWrapper>
          <ResponsiveTypography variant={ResponsiveTypographyVariant.t5SansBold}>
            {startDate}
          </ResponsiveTypography>
        </BookingDatesItemWrapper>
        <BookingDatesItemWrapper>
          <ResponsiveTypography variant={ResponsiveTypographyVariant.body}>
            {i18n('common.nights.text', [booking.duration], translations)}
          </ResponsiveTypography>
        </BookingDatesItemWrapper>
        <ResponsiveTypography variant={ResponsiveTypographyVariant.t5SansBold}>
          {endDate}
        </ResponsiveTypography>
      </BookingDetailsDatesWrapper>
      <BookingDetailsDatesWrapper>
        <BookingDatesItemWrapper>
          <ResponsiveTypography variant={ResponsiveTypographyVariant.body}>
            {i18n('cancellation.details.checkin.date', [], translations)}
          </ResponsiveTypography>
        </BookingDatesItemWrapper>
        <BookingDetailsDatesArrow />
        <ResponsiveTypography variant={ResponsiveTypographyVariant.body}>
          {i18n('cancellation.details.checkout.date', [], translations)}
        </ResponsiveTypography>
      </BookingDetailsDatesWrapper>
      <HorizontalSeperator />
    </div>
  );
};

const BookingDetailsImage: React.FunctionComponent<IBookingDetailsImageProps> = ({ leadImage }) => {
  const device: Device = useDeviceViewport();
  let imageUrl: string = '';
  const size: { width: number; height: number } = BookingDetailsImageSize[device];
  if (leadImage) {
    imageUrl = leadImage.urlWithSize
      ? applySizeToImageUrl(leadImage.urlWithSize, size.width, size.height)
      : leadImage.url;
  }

  return (
    <BookingDetailsImageWrapper data-testid="BookingImage">
      {leadImage && (
        <CustomNormalImage
          fallback={
            <AspectRatioWrapper>
              <LoadingSkeletonSection
                marginBottom={Scale.m}
                width="100%"
                height="100%"
                background={true}
                borderRadius={`${Scale.xxs} ${Scale.xxs} 0 0`}
              />
            </AspectRatioWrapper>
          }
          alt={leadImage.caption}
          size={ImageSizeVariant.fluid}
          srcSet={[{ src: imageUrl }]}
        />
      )}
    </BookingDetailsImageWrapper>
  );
};

const BookingDetailsTransactionInformation: React.FunctionComponent<IBookingDetailsPageProps> = ({
  booking,
}) => {
  const { translations } = React.useContext(TranslationsContext);

  return (
    <BookingDetailsTransactionWrapper>
      <ResponsiveTypography variant={ResponsiveTypographyVariant.bodyBold}>
        {`${i18n('common.transactionId.label', [], translations)}:`}
      </ResponsiveTypography>
      <ResponsiveTypography variant={ResponsiveTypographyVariant.body}>
        {booking.transactionId}
      </ResponsiveTypography>
    </BookingDetailsTransactionWrapper>
  );
};

const BookingDetailsHotelInformation: React.FunctionComponent<IBookingDetailsPageProps> = ({
  booking,
}) => {
  const { translations } = React.useContext(TranslationsContext);
  const hotelAddress: string = getHotelAddressString(booking.hotelDetails.address);

  return (
    <BookingDetailsHotelWrapper>
      <ResponsiveTypography variant={ResponsiveTypographyVariant.bodyBold}>
        {`${i18n('sale.hotelDetails.label', [], translations)}:`}
      </ResponsiveTypography>
      <BookingDetailsHotelDetailslWrapper>
        <ResponsiveTypography variant={ResponsiveTypographyVariant.body}>
          {i18n('hotel.phone', [], translations)}
        </ResponsiveTypography>
        <ResponsiveTypography variant={ResponsiveTypographyVariant.body}>
          {booking.hotelDetails.customerSupportNumber}
        </ResponsiveTypography>
        <ResponsiveTypography variant={ResponsiveTypographyVariant.body}>
          {i18n('hotel.email', [], translations)}
        </ResponsiveTypography>
        <ResponsiveTypography variant={ResponsiveTypographyVariant.body}>
          {booking.hotelDetails.customerSupportEmail}
        </ResponsiveTypography>
        <ResponsiveTypography variant={ResponsiveTypographyVariant.body}>
          {i18n('hotel.address', [], translations)}
        </ResponsiveTypography>
        <ResponsiveTypography variant={ResponsiveTypographyVariant.body}>
          {hotelAddress}
        </ResponsiveTypography>
      </BookingDetailsHotelDetailslWrapper>
    </BookingDetailsHotelWrapper>
  );
};

const BookingDetailsOptions: React.FunctionComponent<IBookingDetailsPageProps> = ({ booking }) => {
  const { translations } = React.useContext(TranslationsContext);
  const [selected, setSelected] = React.useState('');
  const history: History = useHistory();
  const url: string = getPaths(IPageName.bookingsPage)[0];
  const onSelectionChange: (event: React.ChangeEvent<HTMLInputElement>) => void = event => {
    setSelected(event.currentTarget.value);
  };

  const handleClick: () => void = () => {
    switch (selected) {
      case 'PrintBooking':
        window.location.assign(booking.links.printConfirmationEmail);
        break;
      case 'ResendBooking':
        window.location.assign(booking.links.sendConfirmationEmail);
        break;
      case 'PdfInvoice':
        window.location.assign(booking.links.printPdfInvoice);
        break;
      case 'CancelBooking':
        history.push(`${url}/${booking.id}/cancellation`);
        break;
      case 'RebookBooking':
        history.push(`${url}/${booking.id}/cancellation?rebook=true`);
        break;
    }
  };

  const applicationProps: IApplication = React.useContext(ApplicationContext);
  const locale: string = getLocaleSettings(applicationProps.territoryConfig.posLocale);
  const cancelByDate: ICancellationPolicyProperty = booking.cancellationPolicy.properties.find(
    property => property.key === CancellationPolicyKey.DEADLINE
  );
  const formattedCancelByDate: string = cancelByDate
    ? moment(cancelByDate.value)
        .locale(locale)
        .format(DATE_FORMAT)
    : '';

  return (
    <div>
      <RadioButtonList
        framed={true}
        disabled={false}
        onChange={onSelectionChange}
        selected={selected}
      >
        <RadioButton value={'PrintBooking'} dataAttributes={{ testid: 'print-booking-button' }}>
          {i18n('bookings.print.confimation', [], translations)}
        </RadioButton>
        {booking.links.sendConfirmationEmail ? (
          <RadioButton value={'ResendBooking'} dataAttributes={{ testid: 'resend-booking-button' }}>
            {i18n('bookings.resend.confimation', [], translations)}
          </RadioButton>
        ) : (
          <div />
        )}
        {booking.links.printPdfInvoice ? (
          <RadioButton value={'PdfInvoice'} dataAttributes={{ testId: 'pdf-invoice-button' }}>
            {i18n('bookings.print.invoice', [], translations)}
          </RadioButton>
        ) : (
          <div />
        )}
        {booking.cancellationPolicy.isCancellable ? (
          <RadioButton value={'CancelBooking'} dataAttributes={{ testid: 'cancel-booking-button' }}>
            {i18n('bookings.cancel.by', [formattedCancelByDate], translations)}
          </RadioButton>
        ) : (
          <div />
        )}
        {booking.cancellationPolicy.isRebookable && applicationProps.isRebookingEnabled ? (
          <RadioButton value={'RebookBooking'} dataAttributes={{ testid: 'rebook-booking-button' }}>
            {i18n('bookings.change.dates', [], translations)}
          </RadioButton>
        ) : (
          <div />
        )}
      </RadioButtonList>
      {booking.cancellationPolicy.notCancellableReason ? (
        <BookingNotCancellableWrapper>
          <InfoBox subTitle={booking.cancellationPolicy.notCancellableReason} />
        </BookingNotCancellableWrapper>
      ) : null}
      <BookingDetailsButtonWrapper>
        <Button variant={ButtonVariant.primary} onClick={handleClick} disabled={selected === ''}>
          {selected === 'PrintBooking'
            ? i18n('bookings.print.confimation', [], translations)
            : selected === 'ResendBooking'
            ? i18n('bookings.resend.confimation', [], translations)
            : selected === 'CancelBooking'
            ? i18n('bookings.cancel.and.refund', [], translations)
            : selected === 'RebookBooking'
            ? i18n('bookings.continue.change.dates', [], translations)
            : i18n('bookings.continue', [], translations)}
        </Button>
      </BookingDetailsButtonWrapper>
    </div>
  );
};

const BookingDetailsImageSize: {
  [d in Device]: { height: number; width: number };
} = {
  [Device.mobile]: { width: 375, height: 125 },
  [Device.tablet]: { width: 431, height: 143 },
  [Device.desktop]: { width: 431, height: 143 },
};

export {
  BookingDetailsDates,
  BookingDetailsHeader,
  BookingDetailsHotelInformation,
  BookingDetailsImage,
  BookingDetailsOccupancy,
  BookingDetailsOptions,
  BookingDetailsTransactionInformation,
};
